<template>
  <ul
    v-if="totalPages > 1"
    class="pagination"
  >
    <li>
      <button
        class="pagination-previous"
        :class="{ invisible: rawPage === 1 }"
        :aria-hidden="rawPage === 1"
        :aria-label="`Go back to page ${rawPage} of ${totalPages}`"
        @click="handlePaginationBackClick"
      />
    </li>
    <li class="select-container">
      <Form>
        <Dropdown
          v-model="page"
          :options="options"
          label="Page"
          name="reviews-pagination"
        />
      </Form>
    </li>
    <li>
      <button
        class="pagination-next"
        :class="{ invisible: rawPage === totalPages }"
        :aria-hidden="rawPage === totalPages"
        :aria-label="`Go forward to page ${rawPage + 1} of ${totalPages}`"
        @click="handlePaginationNextClick"
      />
    </li>
  </ul>
</template>

<script setup>
import { computed, watch } from 'vue';
import Form from '@atomic-ui/form';
import Dropdown from '@atomic-ui/dropdown';

import { useReviews } from '@composables/reviews/useReviews';
import { useProduct } from '@modules/product';
import { trackEvent } from '@utils/bvPixel';
import { REVIEWS_SEARCH_ID } from '../../constants/reviews';

const { page: rawPage, currentResults, handlePageChange } = useReviews();

const INITIAL_PAGE_ITEMS_COUNT = 8;
const SUBSEQUENT_PAGE_ITEMS_COUNT = 30;

const { productId } = useProduct();

const page = computed({
  get() {
    return String(rawPage.value);
  },
  set(newPage) {
    handlePageChange(newPage);
    // BV track for stats - these run when a page change occurs
    trackEvent('paginate', productId, '', newPage);
  },
});

watch(page, () => {
  if (typeof document !== 'undefined') {
    const reviewsSearch = document.getElementById(REVIEWS_SEARCH_ID);
    reviewsSearch?.scrollIntoView();
  }
});

const totalPages = computed(() => (Number(currentResults.value) <= INITIAL_PAGE_ITEMS_COUNT
  ? 1
  : 1 + Math.ceil(
    (Number(currentResults.value) - INITIAL_PAGE_ITEMS_COUNT)
          / SUBSEQUENT_PAGE_ITEMS_COUNT,
  )));

const options = computed(() => Array.from({ length: totalPages.value })
  .map((_, i) => ({
    value: i + 1,
    displayValue: `${i + 1} of ${totalPages.value}`,
  })));

const handlePaginationNextClick = () => {
  handlePageChange(String(parseInt(page.value, 10) + 1));
  // BV track for stats - these run when a page change occurs
  trackEvent('paginate', productId, 'next');
};

const handlePaginationBackClick = () => {
  handlePageChange(String(parseInt(page.value, 10) - 1));
  // BV track for stats - these run when a page change occurs
  trackEvent('paginate', productId, 'previous');
};
</script>

<style lang="scss" scoped>
@import "@core/csg/src/scss/forms/forms.scss";
@import "@core/csg/src/scss/components/pagination";

@include csg-form-label;
@include csg-form-select;
@include csg-pagination;

.pagination {
  justify-content: center;
}

@include breakpoint(large) {
  .pagination {
    justify-content: flex-end;
  }
}

button:hover {
  cursor: pointer;
}
</style>
